import type { ReactNode } from 'react';
import { Logo } from '../../shared/components/Logo';
import { ThemeToggle } from '../../shared/components/ThemeToggle';

const ONBOARDING_STEPS = [
  { id: 1, label: 'Reset Password' },
  { id: 2, label: 'Business Profile' },
  { id: 3, label: 'Bank & Payouts' },
  { id: 4, label: 'Store Config' },
];

interface OnboardingLayoutProps {
  children: ReactNode;
  currentStep: number;
}

export const OnboardingLayout = ({ children, currentStep }: OnboardingLayoutProps) => {
  return (
    // Fixed overlay covering entire viewport
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Backdrop — token-driven so it dims correctly in both themes */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

      {/* Overlay content — above the backdrop */}
      <div className="relative z-10 flex flex-col h-full overflow-y-auto">

        {/* Sticky progress bar */}
        <div className="sticky top-0 z-20 bg-card/95 backdrop-blur-md border-b border-border px-6 py-4 shadow-sm">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Logo className="h-5 w-auto" />
                <span className="text-[13px] font-[600] text-foreground">Merchant Onboarding</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[12px] text-muted-foreground">Step {currentStep} of {ONBOARDING_STEPS.length}</span>
                {/* Step count auto-adjusts with ONBOARDING_STEPS array length */}
                <ThemeToggle className="p-1.5" />
              </div>
            </div>
            <div className="flex items-center">
              {ONBOARDING_STEPS.map((step, idx) => {
                const done = currentStep > step.id;
                const active = currentStep === step.id;

                return (
                  <div key={step.id} className="flex items-center flex-1 min-w-0">
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-[700] shrink-0 transition-all ${
                        done
                          ? 'bg-green-500 text-white'
                          : active
                          ? 'bg-primary text-primary-foreground ring-2 ring-offset-2 ring-offset-card ring-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {done ? (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : step.id}
                      </div>
                      <span className={`text-[10px] mt-1 text-center truncate w-full ${
                        done ? 'text-green-600 font-[600]' : active ? 'text-foreground font-[600]' : 'text-muted-foreground'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                    {idx < ONBOARDING_STEPS.length - 1 && (
                      <div className={`h-[2px] flex-1 mx-1 mb-4 rounded-full transition-all ${
                        done ? 'bg-green-400' : 'bg-muted'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 flex items-start justify-center px-4 py-10">
          <div className="w-full max-w-2xl">
            {children}
          </div>
        </div>

      </div>
    </div>
  );
};
